import colors from '@honeybook/hbui/dist/styles/colors';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBButton from '@honeybook/hbui/dist/components/HBButton/HBButton';
import Open24 from '@honeybook/icons/dist/icons/Open24';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { PublicTemplateConfigurationProps } from '~/components/Templates/components/FlowTemplates/PublicTemplateConfigurationModal';
import { observer } from 'mobx-react';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import { mediumUp, upToMedium } from '@honeybook/hbui/dist/styles/breakpoints';
import { useWindowSize } from '@honeybook/hbui/dist/components/common/useWindowSize';
import { usePublishedTemplate } from '~/components/Templates/components/FlowTemplates/usePublishedTemplate';
import { useBuildTestId } from '@honeybook/hbui/dist/components/common/useBuildTestId';
import HBSwitch from '@honeybook/hbui/dist/components/HBSwitch/HBSwitch';
import Web16 from '@honeybook/icons/dist/icons/Web16';
import { flexCenterAll, flexColumn } from '@honeybook/hbui/dist/styles/mixins';
import { Loader } from '@honeybook/hbui/dist/components/HBLoader/HBLoader.styles';
import HBLink from '@honeybook/hbui/dist/components/HBLink/HBLink';
import { DeactivatePublicTemplateModal } from '~/components/Templates/components/FlowTemplates/DeactivatePublicTemplateModal';
import Copy16 from '@honeybook/icons/dist/icons/Copy16';
import SellingTemplateModalContent from '~/components/Templates/components/FlowTemplates/SellingTemplateModalContent';
import { useConfigureSellingLeadForm } from '~/components/Templates/components/FlowTemplates/useConfigureSellingLeadForm';
import { useGlobalMX } from '~/providers/mx-global-context/useGlobalMX';
import { useCampaignDetails } from '~/components/Referral/components/ReferralsPage/hooks/useCampaignDetails';

const PublishTemplateModalContent = observer(
    ({ publicConfiguration }: PublicTemplateConfigurationProps) => {
        const { shouldShowSellingToggle, isPartner } =
            useConfigureSellingLeadForm({
                publicConfiguration
            });

        const { t: tPublish } = useTranslation(NAMESPACES.TEMPLATES, {
            keyPrefix: 'share_template.publish_template'
        });

        const {
            userStore: { currUser }
        } = useGlobalMX();

        const { campaignDetails: { max_commission_amount } = {} } =
            useCampaignDetails();

        const { isMediumUp } = useWindowSize();
        const {
            deactivateLink,
            copyLink,
            trackEvent,
            isLoading,
            handleActivateToggleChange,
            isPublished,
            setIsDeactivateModalOpen,
            isDeactivateModalOpen
        } = usePublishedTemplate({
            publicConfiguration
        });

        const openLinkButton = useMemo(() => {
            const button = (
                <ViewLinkButton
                    id=""
                    onClick={() => {
                        trackEvent({ event: 'open share link' });
                        window.open(publicConfiguration?.public_url, '_blank');
                    }}
                    buttonTheme="secondary-solid-secondary"
                    customTheme={{
                        background: colors.neutral100,
                        color: colors.black,
                        fontSize: '16px'
                    }}
                    size="md"
                >
                    <Open24 color={colors.black} />
                    {!isMediumUp && tPublish('open_link_cta')}
                </ViewLinkButton>
            );
            return !isMediumUp ? (
                button
            ) : (
                <HBTooltip placement="top" target={button}>
                    {tPublish('open_link_tooltip')}
                </HBTooltip>
            );
        }, [isMediumUp, publicConfiguration?.public_url, tPublish, trackEvent]);

        const copyButtonIcon = useMemo(() => {
            return isMediumUp ? null : <Copy16 color={colors.white} />;
        }, [isMediumUp]);

        const templateLinkTestId = useBuildTestId({
            componentName: 'PublishedTemplateModalContent',
            identifier: 'template',
            suffix: 'link'
        });

        if (isPartner) {
            return (
                <SellingTemplateModalContent
                    publicConfiguration={publicConfiguration}
                />
            );
        }

        return (
            <ModalContent>
                <ActivateContainer>
                    <ActivateToggle>
                        <ActivateTitleContainer>
                            <Web16 color={colors.black} />
                            <HBText type="emphasis16">
                                {tPublish('activate_link_title')}
                            </HBText>
                        </ActivateTitleContainer>
                        {isLoading ? (
                            <Loader color={colors.black} size="small" />
                        ) : (
                            <HBSwitch
                                size="md"
                                id="generate-link"
                                testIdName="generate-link"
                                checked={isPublished}
                                label={tPublish('activate_link_title')}
                                name={tPublish('activate_link_title')}
                                onChange={handleActivateToggleChange}
                            />
                        )}
                    </ActivateToggle>
                    <ActivateDescription color="neutral800" type="body16">
                        {tPublish('activate_link_description')}
                    </ActivateDescription>
                </ActivateContainer>
                {isPublished && (
                    <LinkContainer>
                        <HBTooltip
                            disabled={!isMediumUp}
                            placement="top"
                            target={
                                <TemplateLink>
                                    <Link
                                        type="body16"
                                        testId={templateLinkTestId}
                                    >
                                        {publicConfiguration?.public_url}
                                    </Link>
                                </TemplateLink>
                            }
                            maxWidth="100%"
                        >
                            {publicConfiguration?.public_url}
                        </HBTooltip>
                        <LinkButtons>
                            {openLinkButton}
                            <LinkButton
                                onClick={() => {
                                    copyLink(publicConfiguration.public_url);
                                }}
                                size="md"
                                customTheme={{
                                    fontSize: '16px',
                                    fontWeight: 700
                                }}
                                testIdName="copy-link"
                            >
                                {copyButtonIcon}
                                {tPublish('copy_cta')}
                            </LinkButton>
                        </LinkButtons>
                    </LinkContainer>
                )}
                {!shouldShowSellingToggle && currUser?.isSubscribed && (
                    <LearnMoreContainer>
                        <LearnMoreText type="body16">
                            {tPublish(
                                currUser?.isEducator
                                    ? 'referral_discount_description_educators'
                                    : 'referral_discount_description',
                                { amount: max_commission_amount }
                            )}
                            <HBLink
                                isNewTab
                                color={colors.blue20}
                                testIdName="referral-learn-more"
                                noUnderline
                                href="https://help.honeybook.com/en/articles/2209080-referral-program-faq"
                                textType="emphasis16"
                            >
                                {!isMediumUp ? <br /> : ' '}
                                {tPublish('learn_more')}
                            </HBLink>
                        </LearnMoreText>
                    </LearnMoreContainer>
                )}

                <DeactivatePublicTemplateModal
                    deactivateLink={deactivateLink}
                    isOpen={isDeactivateModalOpen}
                    closeDeactivateModal={() => setIsDeactivateModalOpen(false)}
                />
            </ModalContent>
        );
    }
);

export default PublishTemplateModalContent;

export const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
`;

export const TemplateLink = styled.div`
    background-color: ${colors.neutral100};
    border-radius: 4px;
    align-content: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: none;
    overflow: hidden;
    height: 42px;
    ${mediumUp} {
        max-width: 258px;
    }
`;

const ActivateTitleContainer = styled.div`
    ${flexCenterAll}
    gap: 6px;
`;

const ActivateContainer = styled.div`
    width: 100%;
    ${flexColumn}
    gap: 7px;
`;

const ActivateDescription = styled(HBText)`
    max-width: 361px;
`;

const ActivateToggle = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const LinkContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    width: 100%;
    ${upToMedium} {
        flex-direction: column;
        gap: 16px;
    }
`;

export const OptionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ToggleContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const LearnMoreContainer = styled.div`
    border: 1px solid ${colors.neutral300};
    padding: 16px;
    border-radius: 4px;
`;

export const LearnMoreText = styled(HBText)`
    max-width: 400px;
`;

export const ToggleTitle = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;

export const Link = styled(HBText)`
    padding: 0 8px;
`;

export const LinkButtons = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

export const LinkButton = styled(HBButton).attrs({
    textTransform: 'uppercase'
})`
    display: flex;
    gap: 8px;
    ${upToMedium} {
        flex-grow: 1;
    }
`;

export const ViewLinkButton = styled(LinkButton)`
    ${mediumUp} {
        width: 42px;
        border-radius: 4px;
    }
`;
